
export const SectionHeader = ({ title, icon, description, badge }) => (
  <div className="mb-4 sm:mb-6">
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
      <div className="flex items-center gap-2 sm:gap-3">
        {icon && (
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-gray-800 to-black flex items-center justify-center shadow-lg shadow-gray-300 shrink-0">
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={icon}
              />
            </svg>
          </div>
        )}
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-800">
            {title}
          </h2>
          {description && (
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
              {description}
            </p>
          )}
        </div>
      </div>
      {badge && (
        <span className="sm:ml-auto self-start sm:self-auto">{badge}</span>
      )}
    </div>
  </div>
);

export const FormSection = ({ title, icon, children, className = "" }) => (
  <section className={`mb-10 ${className}`}>
    {title && (
      <div className="flex items-center gap-3 mb-5 pb-3 border-b border-gray-100">
        {icon && (
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gray-800 to-black flex items-center justify-center">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={icon}
              />
            </svg>
          </div>
        )}
        <h3 className="text-base font-semibold text-gray-800">{title}</h3>
      </div>
    )}
    {children}
  </section>
);

export const FormField = ({
  label,
  children,
  required = false,
  description,
  error,
}) => (
  <div className="mb-5">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {description && (
      <p className="text-xs text-gray-500 mt-1.5">{description}</p>
    )}
    {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
  </div>
);

export const TextInput = ({
  value,
  onChange,
  placeholder,
  type = "text",
  textarea = false,
  className = "",
  rows = 3,
  ...rest // any other attribute (aria-*, inputMode, autoComplete, id, ...) goes on the field
}) => {
  const baseStyles =
    "w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow placeholder:text-gray-400";

  if (textarea) {
    return (
      <textarea
        {...rest}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        className={`${baseStyles} resize-y min-h-[100px] ${className}`}
      />
    );
  }

  return (
    <input
      {...rest}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`${baseStyles} ${className}`}
    />
  );
};

export const SelectInput = ({ value, onChange, children, className = "" }) => (
  <select
    value={value}
    onChange={onChange}
    className={`w-full pl-4 pr-10 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white transition-shadow appearance-none cursor-pointer ${className}`}
    style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
      backgroundSize: "1.25rem",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 0.625rem center",
    }}
  >
    {children}
  </select>
);

export const ColorInput = ({ value, onChange }) => (
  <div className="flex items-center gap-2 sm:gap-3">
    <input
      type="color"
      value={value}
      onChange={onChange}
      className="w-10 h-10 sm:w-12 sm:h-11 rounded-xl border-2 border-gray-200 cursor-pointer hover:border-gray-400 transition-colors shrink-0"
    />
    <input
      type="text"
      value={value}
      onChange={onChange}
      className="flex-1 min-w-0 px-3 py-2 sm:py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-900 uppercase"
      placeholder="#000000"
    />
  </div>
);

export const Toggle = ({ checked, onChange, label, description, disabled = false }) => {
  if (label) {
    return (
      <label className={`flex items-center justify-between gap-2 p-2 sm:p-3 rounded-xl transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer group hover:bg-gray-50"}`}>
        <div className="flex-1 min-w-0">
          <span className="text-sm text-gray-700 font-medium group-hover:text-gray-900 transition-colors block">
            {label}
          </span>
          {description && (
            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
          )}
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && onChange(!checked)}
          className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 text-left inline-flex items-center shrink-0 cursor-pointer ${
            checked ? "bg-gray-900" : "bg-gray-200"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <span
            className={`inline-block w-5 h-5 rounded-full bg-white shadow transform transition-transform duration-200 ease-in-out ${
              checked ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
      </label>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 text-left inline-flex items-center cursor-pointer ${
        checked ? "bg-gray-900" : "bg-gray-200"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <span
        className={`inline-block w-5 h-5 rounded-full bg-white shadow transform transition-transform duration-200 ease-in-out ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
};

export const Button = ({
  variant = "primary",
  size = "md",
  children,
  onClick,
  className = "",
  disabled = false,
  type = "button",
}) => {
  const baseStyles =
    "inline-flex items-center justify-center font-medium rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2";

  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-5 py-2.5 text-sm",
    lg: "px-6 py-3 text-base",
  };

  const variants = {
    primary:
      "bg-gradient-to-r from-gray-800 to-black text-white hover:from-gray-900 hover:to-gray-900 shadow-lg shadow-gray-300",
    secondary: "bg-gray-100 text-gray-700 hover:bg-gray-200",
    success: "bg-emerald-500 text-white hover:bg-emerald-600",
    danger: "bg-red-500 text-white hover:bg-red-600",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
    ghost: "text-gray-600 hover:bg-gray-100",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${baseStyles} ${sizes[size]} ${variants[variant]} ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      } ${className}`}
    >
      {children}
    </button>
  );
};

export const TagItem = ({ children, onRemove, color = "default" }) => {
  const colors = {
    default: "bg-gray-100 text-gray-700 border-gray-200",
    indigo: "bg-gray-100 text-gray-800 border-gray-300",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };

  return (
    <div
      className={`flex items-center justify-between px-4 py-2.5 rounded-xl border transition-colors group hover:shadow-sm ${colors[color]}`}
    >
      {children}
      {onRemove && (
        <button
          onClick={onRemove}
          className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors ml-2 shrink-0"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export const Card = ({ title, icon, children, className = "", actions }) => (
  <div
    className={`bg-white rounded-2xl border border-gray-200 overflow-hidden ${className}`}
  >
    {(title || icon || actions) && (
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          {icon && (
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gray-800 to-black flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={icon}
                />
              </svg>
            </div>
          )}
          {title && (
            <h3 className="text-base font-semibold text-gray-800">{title}</h3>
          )}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    )}
    <div className="p-6">{children}</div>
  </div>
);

export const EmptyState = ({ icon, title, description, action }) => (
  <div className="text-center py-12 px-6 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
    {icon && (
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-400">
        <svg
          className="w-8 h-8"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={icon}
          />
        </svg>
      </div>
    )}
    <h3 className="text-base font-semibold text-gray-700 mb-1">{title}</h3>
    <p className="text-sm text-gray-500 mb-4">{description}</p>
    {action && action}
  </div>
);

// disabled: the choice is shown (dimmed) but cannot be changed.
export const RadioGroup = ({
  value,
  onChange,
  options,
  name,
  horizontal = false,
  disabled = false,
}) => (
  <div className={`flex ${horizontal ? "flex-row gap-4" : "flex-col gap-2"} ${disabled ? "opacity-50" : ""}`}>
    {options.map((opt) => (
      <label
        key={opt.value}
        className={`flex items-center gap-2 ${disabled ? "cursor-not-allowed" : "cursor-pointer"} group relative`}
      >
        <input
          type="radio"
          name={name}
          value={opt.value}
          checked={value === opt.value}
          disabled={disabled}
          onChange={() => onChange(opt.value)}
          className="peer sr-only"
        />
        <span className="w-4 h-4 rounded-full border-2 border-gray-300 peer-checked:border-gray-900 peer-checked:bg-gray-900 relative transition-all">
          <span className="absolute inset-0 m-auto w-1.5 h-1.5 rounded-full bg-white opacity-0 peer-checked:opacity-100 transition-opacity" />
        </span>
        <span className={`text-sm text-gray-600 ${disabled ? "" : "group-hover:text-gray-800"}`}>
          {opt.label}
        </span>
      </label>
    ))}
  </div>
);

export const CheckboxCard = ({ checked, onChange, children, icon }) => (
  <label
    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
      checked
        ? "border-gray-900 bg-gray-100"
        : "border-gray-200 hover:border-gray-300"
    }`}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="sr-only"
    />
    <div
      className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-colors ${
        checked ? "bg-gray-900 border-gray-900" : "border-gray-300"
      }`}
    >
      {checked && (
        <svg
          className="w-3 h-3 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M5 13l4 4L19 7"
          />
        </svg>
      )}
    </div>
    {icon && (
      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={icon}
          />
        </svg>
      </div>
    )}
    <span className="text-sm font-medium text-gray-700">{children}</span>
  </label>
);

export const Icon = ({ name, className = "w-5 h-5" }) => {
  const icons = {
    plus: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4v16m8-8H4"
      />
    ),
    trash: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 011-1h2a1 1 0 011 1v3M4 7h16"
      />
    ),
    edit: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    ),
    check: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    ),
    close: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    ),
    chat: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    ),
    poll: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    ),
    settings: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
    ),
    clock: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    ),
    user: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    ),
    speaker: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-2.707 1.707L5.586 15z"
      />
    ),
    video: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    ),
    link: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
      />
    ),
    copy: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    ),
    "chevron-left": (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 19l-7-7 7-7"
      />
    ),
    "chevron-right": (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    ),
  };

  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      {icons[name] || (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      )}
    </svg>
  );
};

export const StatusBadge = ({ status, children }) => {
  const styles = {
    active: "bg-gray-200 text-gray-800 border-gray-300",
    inactive: "bg-gray-100 text-gray-600 border-gray-200",
    pending: "bg-purple-100 text-purple-700 border-purple-200",
    error: "bg-red-100 text-red-700 border-red-200",
    success: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border ${
        styles[status] || styles.inactive
      }`}
    >
      {children}
    </span>
  );
};

export const InfoBox = ({ variant = "info", children, className = "" }) => {
  const variants = {
    info: "bg-gray-100 border-gray-300 text-gray-900",
    success: "bg-emerald-50 border-emerald-200 text-emerald-800",
    warning: "bg-amber-50 border-amber-200 text-amber-800",
    error: "bg-red-50 border-red-200 text-red-800",
  };

  return (
    <div className={`p-4 rounded-xl border ${variants[variant]} ${className}`}>
      {children}
    </div>
  );
};

export const ProgressBar = ({ value, max = 100, variant = "default" }) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  const variants = {
    default:
      percentage === 100
        ? "bg-emerald-500"
        : percentage > 0
        ? "bg-gray-900"
        : "bg-gray-300",
    success: "bg-emerald-500",
    warning: "bg-amber-500",
    danger: "bg-red-500",
  };

  return (
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-300 ${variants[variant]}`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};
